'use client'

import { useCurrentLocation } from '@/src/hooks'

import { Alert, Card, Tag, Typography } from 'antd'
import {
  PhoneButtonInfo,
  PhysicianAddress,
  PhysicianLocationMap,
} from '@/src/components'

import { DISCLAIMER_MESSAGES } from '@/src/data'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { Physician } from '@/src/interfaces'

const { Text, Title } = Typography

const cardStyle = {
  border: '1px solid #00000040',
  scrollMarginTop: '20px',
}

interface SectionProps {
  physician: Physician
}

export const ProfileSection = ({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) => (
  <div>
    <Title level={5}>{title}</Title>
    {children}
  </div>
)

export const OverviewSection = ({ physician }: SectionProps) => (
  <Card id='part-1' style={cardStyle}>
    <div className='flex flex-col gap-8'>
      {physician.gender && (
        <ProfileSection title='Gender'>
          <Text>{physician.gender}</Text>
        </ProfileSection>
      )}
      {physician.specialty && (
        <ProfileSection title='Specialty'>
          <div className='flex flex-col'>
            <Text>{physician.specialty}</Text>
            {physician.general_specialty &&
              physician.general_specialty !== physician.specialty && (
                <Text>{physician.general_specialty}</Text>
              )}
          </div>
        </ProfileSection>
      )}
      <div>
        {physician.telehealth_appointment && (
          <Tag color='#4DA0AE'>
            <FontAwesomeIcon icon='desktop' className='mr-2' />
            Offers Telehealth
          </Tag>
        )}
        {physician.booking_url && (
          <Tag color='#4DA0AE'>
            <FontAwesomeIcon icon='calendar-check' className='mr-2' />
            Online Scheduling
          </Tag>
        )}
      </div>
    </div>
  </Card>
)

export const LocationSection = ({ physician }: SectionProps) => {
  const { geoLocation } = useCurrentLocation()

  return (
    <Card id='part-2' style={cardStyle}>
      <div className='flex flex-col gap-8'>
        <ProfileSection title='Primary Practice Location'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-2 print:gap-2'>
            <div className='flex flex-col gap-4'>
              <div>
                <PhysicianAddress
                  physician={physician}
                  isProfileCard={true}
                  geoLocation={geoLocation}
                />
              </div>

              {physician.phone && <PhoneButtonInfo physician={physician} />}
            </div>
            <PhysicianLocationMap
              address={physician.address}
              physicianName={physician.full_name}
            />
          </div>
        </ProfileSection>
      </div>
    </Card>
  )
}

export const ExperienceSection = ({ physician }: SectionProps) => (
  <Card id='part-3' style={cardStyle}>
    <div className='flex flex-col gap-8'>
      {physician.board_certification && (
        <ProfileSection title='Board Certification'>
          <Text>{physician.board_certification}</Text>
        </ProfileSection>
      )}
      {physician.trainings && (
        <ProfileSection title='Training'>
          <Text>{physician.trainings}</Text>
        </ProfileSection>
      )}
    </div>
  </Card>
)

export const InsuranceSection = ({ physician }: SectionProps) => (
  <Card id='part-4' style={cardStyle}>
    <div className='flex flex-col gap-8'>
      <Alert
        message={DISCLAIMER_MESSAGES.insuranceDisclaimer}
        type='info'
        showIcon
      />
      <InsuranceList
        title='In-Network Insurances'
        insurances={physician.insurance_accepted}
      />
    </div>
  </Card>
)

export const AffiliatedInstitutionsHospitals = ({
  physician,
}: SectionProps) => (
  <Card id='part-5' style={cardStyle}>
    <div className='flex flex-col gap-8'>
      {physician.institution_name && (
        <ProfileSection title='Affiliated Institutions & Hospitals'>
          <Text>{physician.institution_name}</Text>
        </ProfileSection>
      )}
    </div>
  </Card>
)

const InsuranceList: React.FC<{
  title: string
  insurances: string | string[]
}> = ({ title, insurances }) => (
  <div>
    <Title level={5}>{title}</Title>
    <ul className='list-disc ml-4'>
      {(typeof insurances === 'string' ? insurances.split(',') : insurances)
        .map((insurance) => insurance.trim())
        .sort((a, b) => a.localeCompare(b))
        .map((insurance, index) => (
          <li key={index}>
            <Text>{insurance}</Text>
          </li>
        ))}
    </ul>
  </div>
)
